import axios from 'axios';
import API_BASE_URL from '../config/api.js';

/**
 * Normalizes the catalog response into a consistent object shape.
 * @param {any} data Raw response data from the API
 * @returns {{tracks: Array, playlists: Array}} Normalized catalog object
 */
const normalizeCatalogResponse = (data) => {
  const body = typeof data === 'string' ? JSON.parse(data) : data;

  if (Array.isArray(body)) {
    return { tracks: body, playlists: [] };
  }

  return {
    tracks: body?.tracks || [],
    playlists: body?.playlists || [],
  };
};

/**
 * Fetches tracks from the librosa catalog endpoint.
 * Accepts optional filters that will be sent as query params so the backend
 * can perform server-side filtering. If the backend ignores these params,
 * the full catalog will still be returned and can be filtered client-side.
 * @param {object} params - Optional filters/search terms.
 * @returns {Promise<{tracks: Array, playlists: Array}>} Catalog data object.
 */
export const fetchCatalog = async (params = {}) => {
  try {
    // Use the librosa catalog endpoint for audio analysis data
    const response = await axios.get(`${API_BASE_URL}/catalog/librosa`, { params });
    return normalizeCatalogResponse(response.data.body ?? response.data);
  } catch (error) {
    console.error('Error fetching catalog:', error);
    // Return sample catalog data as fallback
    return {
      tracks: getFallbackCatalogData(),
      playlists: [],
    };
  }
};

const getFallbackCatalogData = () => [
      {
        id: "okina-01",
        track_title: "999,999",
        album: "Okina Sakana",
        artist: "Rue de Vivre",
        bpm: 128,
        duration: "2:12",
        genre: "Hip Hop",
        mood: "Money/Business",
        spotify_url: "https://open.spotify.com/track/0jGJEdoVlvOWVt3C9GcFJQ",
        assets: {
          clip_wav: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/clips/999999-clip.wav"
        }
      },
      {
        id: "okina-02",
        track_title: "OG Bashment",
        album: "Okina Sakana", 
        artist: "Rue de Vivre",
        bpm: 135,
        duration: "3:27",
        genre: "Dancehall",
        mood: "Party/Celebration",
        spotify_url: "https://open.spotify.com/track/5LsoV9QPzdaZ1T3U3F7pFd",
        assets: {
          clip_wav: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/clips/ogbashment-clip.wav"
        }
      },
      {
        id: "okina-03",
        track_title: "Monday",
        album: "Okina Sakana",
        artist: "Rue de Vivre", 
        bpm: 102,
        duration: "3:18",
        genre: "Hip Hop",
        mood: "Monday/Work",
        spotify_url: "https://open.spotify.com/track/16y1FUke3lRQi28TZQxd1C",
        assets: {
          clip_wav: "https://decodedmusic-artist-catalog-usw2.s3.us-west-2.amazonaws.com/okina-sakana/clips/monday-clip.wav"
        }
      }
    ];

/**
 * Wrapper for the simple catalog view. Currently identical to fetchCatalog but
 * kept separate for clarity and future expansion.
 */
export const getSimpleCatalog = (params = {}) => fetchCatalog(params);

/**
 * Wrapper for the showcase catalog view.
 */
export const getShowcaseCatalog = (params = {}) => fetchCatalog(params);

/**
 * Wrapper for the professional catalog view.
 */
export const getProfessionalCatalog = (params = {}) => fetchCatalog(params);

/**
 * Backwards‑compatibility helpers referenced by deployment scripts.
 */
export const getDetailedCatalog = (params = {}) => fetchCatalog(params);
export const getRealCatalog = (params = {}) => fetchCatalog(params);

/**
 * Adds a new track to your catalog.
 * This function calls POST /todos and triggers the 'AddTodoFunction'.
 * @param {object} trackData - The full JSON object for your track, as designed in your notes.
 * @returns {Promise<object>} A promise that resolves to the newly created track object.
 */
export const addTrack = async (trackData) => {
  // IMPORTANT: The blueprint's DynamoDB table requires a primary key named 'id'.
  // We must map your 'track_id' to 'id' before sending.
  const payload = {
    ...trackData,
    id: trackData.track_id, // Map your track_id to the required 'id' field
  };

  try {
    const response = await axios.post(`${API_BASE_URL}/todos`, payload);
    return typeof response.data.body === 'string'
      ? JSON.parse(response.data.body)
      : response.data.body;
  } catch (error) {
    console.error("Error adding track:", error);
    throw error; // Re-throw the error to be handled by the UI
  }
};

/**
 * Fetches a single track by its ID.
 * Calls: GET /todos/{id}
 * @param {string} trackId - The ID of the track to fetch.
 */
export const getTrackById = async (trackId) => {
  try {
    const response = await axios.get(`${API_BASE_URL}/todos/${trackId}`);
    return typeof response.data.body === 'string'
      ? JSON.parse(response.data.body)
      : response.data.body;
  } catch (error) {
    console.error(`Error fetching track ${trackId}:`, error);
    throw error;
  }
};

/**
 * Updates an existing track.
 * Calls: PUT /todos/{id}
 * @param {string} trackId - The ID of the track to update.
 * @param {object} trackData - The full JSON object with the updated data.
 */
export const updateTrack = async (trackId, trackData) => {
  try {
    const response = await axios.put(`${API_BASE_URL}/todos/${trackId}`, trackData);
    return typeof response.data.body === 'string'
      ? JSON.parse(response.data.body)
      : response.data.body;
  } catch (error) {
    console.error(`Error updating track ${trackId}:`, error);
    throw error;
  }
};

/**
 * Deletes a track from the catalog.
 * This function calls DELETE /todos/{id} and triggers the 'DeleteTodoFunction'.
 * @param {string} trackId - The ID of the track to delete.
 */
export const deleteTrack = async (trackId) => {
  try {
    await axios.delete(`${API_BASE_URL}/todos/${trackId}`);
  } catch (error) {
    console.error(`Error deleting track ${trackId}:`, error);
    throw error;
  }
};
